import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Link, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { CommercialReportService } from '../../services/commercialReport.service';
import {
    IExpAgingReport, IIncentiveStatusReport, ILcPositionReport, ILcPositionRow, IncentiveClaimStatus,
} from '../../interfaces/commercial.interface';
import StatTile, { StatTone } from '../dashboard/charts/StatTile';
import Columns from '../dashboard/charts/Columns';
import HBars from '../dashboard/charts/HBars';
import HStack100 from '../dashboard/charts/HStack100';
import { compactMoney } from '../dashboard/charts/axisPadding';
import { money } from '../statutoryRegisters/shared';
import { enumLabel } from '../../utils/garmentsLabels';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import MyBreakdownsDashSection from '../maintenance/MyBreakdownsDashSection';
import MyWidgetsSection from '../dashboard/board/MyWidgetsSection';

const CLOSED_LC: string[] = ['REALIZED', 'CLOSED'];
const PENDING_CLAIMS: IncentiveClaimStatus[] = ['SUBMITTED', 'UNDER_AUDIT', 'APPROVED'];
const OVERDUE_DAYS = 90;
const EXPIRY_WINDOW_DAYS = 30;

const Card: React.FC<{ title: string; sub?: string; children: React.ReactNode; testId?: string; 'data-help-section'?: string }> = ({ title, sub, children, testId, ...rest }) => (
    <Paper variant="outlined" data-testid={testId} sx={{ p: 2, flex: '1 1 340px', minWidth: 0 }} {...rest}>
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        {sub && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{sub}</Typography>}
        <Box sx={{ mt: 1 }}>{children}</Box>
    </Paper>
);

const Empty: React.FC<{ text: string }> = ({ text }) => (
    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{text}</Typography>
);

const monthKey = (daysFromNow: number): string => {
    const d = new Date();
    d.setDate(d.getDate() + daysFromNow);
    return toLocalIsoDate(d).slice(0, 7);
};

/**
 * Commercial officer landing dashboard. Computed client-side from the three T11 report endpoints.
 * Money is never summed across currencies: LC/EXP figures are shown for one selected currency
 * (default = the one with the most LCs); cash-incentive claims are BDT-only (backend R-27).
 */
const CommercialDashboard: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [loading, setLoading] = useState(true);
    const [lc, setLc] = useState<ILcPositionReport | null>(null);
    const [aging, setAging] = useState<IExpAgingReport | null>(null);
    const [inc, setInc] = useState<IIncentiveStatusReport | null>(null);
    const [currency, setCurrency] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.allSettled([
            CommercialReportService.getLcPositionReport(institutionId),
            CommercialReportService.getUnrealizedExpAgingReport(institutionId),
            CommercialReportService.getIncentiveClaimStatusReport(institutionId),
        ]).then(([a, b, c]) => {
            if (cancelled) return;
            setLc(a.status === 'fulfilled' ? a.value : null);
            setAging(b.status === 'fulfilled' ? b.value : null);
            setInc(c.status === 'fulfilled' ? c.value : null);
            if (a.status === 'rejected') toast.showApiError(a.reason, 'Failed to load LC position');
            if (b.status === 'rejected') toast.showApiError(b.reason, 'Failed to load unrealized EXP aging');
            if (c.status === 'rejected') toast.showApiError(c.reason, 'Failed to load incentive claims');
            setLoading(false);
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const currencies = useMemo(() => {
        const totals = lc?.grandTotal ?? [];
        return [...totals].sort((x, y) => y.lcCount - x.lcCount).map(t => t.currencyCode);
    }, [lc]);
    const cur = currency && currencies.includes(currency) ? currency : currencies[0] ?? null;

    const total = lc?.grandTotal.find(t => t.currencyCode === cur) ?? null;
    const openRows: ILcPositionRow[] = useMemo(
        () => (lc?.rows ?? []).filter(r => r.currencyCode === cur && !CLOSED_LC.includes(r.status)),
        [lc, cur]
    );
    const openValue = openRows.reduce((s, r) => s + r.lcAmount, 0);
    const expiring = useMemo(
        () => openRows.filter(r => r.daysToExpiry != null && r.daysToExpiry >= 0 && r.daysToExpiry <= EXPIRY_WINDOW_DAYS)
            .sort((x, y) => (x.daysToExpiry ?? 0) - (y.daysToExpiry ?? 0)),
        [openRows]
    );
    const lapsed = openRows.filter(r => r.daysToExpiry != null && r.daysToExpiry < 0).length;

    const agingBuckets = useMemo(() => {
        const present = (aging?.buckets ?? []).filter(b => b.groupType === 'GRAND' && b.currencyCode === cur)
            .sort((x, y) => x.startDay - y.startDay);
        // The report only returns buckets that hold money; show an empty bar for any skipped day
        // range so the aging reads left-to-right without silent jumps (e.g. 31-60 -> Over 120).
        const filled: typeof present = [];
        present.forEach((b, i) => {
            const prev = present[i - 1];
            if (prev && prev.endDayExclusive != null && prev.endDayExclusive < b.startDay) {
                filled.push({ ...prev, bucketLabel: `${prev.endDayExclusive}-${b.startDay - 1}`, startDay: prev.endDayExclusive,
                    endDayExclusive: b.startDay, count: 0, amount: 0 });
            }
            filled.push(b);
        });
        return filled;
    }, [aging, cur]);
    const overdueAmount = agingBuckets.filter(b => b.startDay >= OVERDUE_DAYS).reduce((s, b) => s + b.amount, 0);
    const overdueRows = useMemo(
        () => (aging?.rows ?? []).filter(r => r.currencyCode === cur && r.unrealizedAmount > 0)
            .sort((x, y) => y.daysOutstanding - x.daysOutstanding).slice(0, 5),
        [aging, cur]
    );

    const buyers = useMemo(() => {
        const m = new Map<string, number>();
        openRows.forEach(r => m.set(r.buyerName, (m.get(r.buyerName) ?? 0) + r.lcAmount));
        return Array.from(m, ([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 6);
    }, [openRows]);

    const utilRows = useMemo(
        () => [...openRows].sort((a, b) => b.lcAmount - a.lcAmount).slice(0, 6).map(r => {
            const shipped = Math.min(Math.max(r.expDeclared, 0), r.lcAmount);
            return { label: r.lcNumber, segments: [shipped, Math.max(r.lcAmount - shipped, 0)] };
        }),
        [openRows]
    );

    const expiryByMonth = useMemo(() => {
        const m = new Map<string, number>();
        openRows.forEach(r => {
            if (r.daysToExpiry != null && r.daysToExpiry >= 0) {
                const k = monthKey(r.daysToExpiry);
                m.set(k, (m.get(k) ?? 0) + r.lcAmount);
            }
        });
        return Array.from(m, ([label, value]) => ({ label, value })).sort((a, b) => a.label.localeCompare(b.label)).slice(0, 8);
    }, [openRows]);

    const incentives = inc?.enabled ? inc : null;
    const pending = (incentives?.byStatus ?? []).filter(s => PENDING_CLAIMS.includes(s.status));
    const pendingCount = pending.reduce((s, x) => s + x.count, 0);
    const pendingAmount = pending.reduce((s, x) => s + x.notYetReceivedAmount, 0);
    const claimBars = (incentives?.byStatus ?? []).filter(s => s.count > 0)
        .map(s => ({ label: enumLabel(s.status), value: s.count }));

    if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>;

    if (!lc && !aging && !inc) {
        return <Alert severity="error" data-testid="commercial-dashboard-error" data-help-section="commercial.dashboard.notice">The commercial dashboard could not be loaded. Please try again.</Alert>;
    }

    const noLcData = !total;
    const utilPct = total && total.lcAmount > 0 ? Math.round((total.expDeclared / total.lcAmount) * 100) : 0;
    const unrealTone: StatTone = overdueAmount > 0 ? 'warning' : 'default';
    const fmt = (n: number) => money(n);

    return (
        <Box data-testid="commercial-dashboard" data-help-page="commercial.dashboard.page">
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight={600}>Commercial Dashboard</Typography>
                {currencies.length > 1 && cur && (
                    <ToggleButtonGroup data-help="commercial.dashboard.currency" size="small" exclusive value={cur} onChange={(_, v) => v && setCurrency(v)} aria-label="Currency">
                        {currencies.map(c => <ToggleButton key={c} value={c}>{c}</ToggleButton>)}
                    </ToggleButtonGroup>
                )}
            </Stack>

            {(!lc || !aging || !inc) && (
                <Alert severity="warning" sx={{ mb: 2 }} data-testid="commercial-dashboard-partial" data-help-section="commercial.dashboard.notice">
                    Some figures could not be loaded and are hidden: {[!lc && 'LC position', !aging && 'unrealized EXP', !inc && 'incentive claims'].filter(Boolean).join(', ')}.
                </Alert>
            )}
            {lc?.truncated && (
                <Alert severity="info" sx={{ mb: 2 }} data-help-section="commercial.dashboard.notice">
                    LC lists and charts cover {lc.rows.length} of {lc.totalMatchingRows} LCs; total tiles cover all of them.
                </Alert>
            )}
            {currencies.length > 1 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Showing {cur} figures only — currencies are never added together. Incentive claims are BDT.
                </Typography>
            )}

            {lc && noLcData && (
                <Alert severity="info" sx={{ mb: 2 }} data-testid="commercial-dashboard-empty" data-help-section="commercial.dashboard.empty">
                    No export LCs yet. Register a received LC to see exposure, utilization and expiry insights here.
                    {' '}<Link component={RouterLink} to="/commercial/lc">Go to Export LCs</Link>
                </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 1, mb: 2 }} data-help-section="commercial.dashboard.kpis">
                <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {lc && (
                        <>
                            <StatTile data-help="commercial.dashboard.tile.openLcs" label="Open export LCs" value={openRows.length}
                                sub={`Value ${cur ?? ''} ${fmt(openValue)}`.trim()} />
                            <StatTile data-help="commercial.dashboard.tile.lcShipped" label="LC value shipped" value={`${utilPct}%`} meter={utilPct} tone="good"
                                sub={total ? `Balance ${fmt(Math.max(total.lcAmount - total.expDeclared, 0))}` : undefined} />
                            <StatTile data-help="commercial.dashboard.tile.b2b" label="Back-to-back exposure" unit={cur ?? undefined} value={fmt(total?.b2bOpened ?? 0)}
                                sub={`Utilized ${fmt(total?.b2bUtilized ?? 0)}`} />
                            <StatTile data-help="commercial.dashboard.tile.lcExpiring" label="LCs expiring in 30 days" value={expiring.length}
                                tone={expiring.length > 0 ? 'warning' : 'default'}
                                sub={lapsed > 0 ? `${lapsed} already lapsed` : undefined} />
                        </>
                    )}
                    {aging && (
                        <StatTile data-help="commercial.dashboard.tile.unrealized" label="Unrealized export proceeds" unit={cur ?? undefined} value={fmt(agingBuckets.reduce((s, b) => s + b.amount, 0))}
                            tone={unrealTone} sub={`${fmt(overdueAmount)} older than ${OVERDUE_DAYS} days`} />
                    )}
                    {inc && (
                        <StatTile data-help="commercial.dashboard.tile.incentive" label="Cash incentive pending" value={incentives ? pendingCount : '—'}
                            unit={incentives ? undefined : undefined}
                            sub={incentives ? `BDT ${fmt(pendingAmount)} not yet received` : 'Incentives not enabled'}
                            tone={pendingCount > 0 ? 'warning' : 'default'} />
                    )}
                </Stack>
            </Paper>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                {aging && (
                    <Card data-help-section="commercial.dashboard.agingChart" title="Unrealized EXP aging" sub={`Amount outstanding by days since EXP (${cur ?? '—'})`}>
                        {agingBuckets.length === 0 ? <Empty text="No unrealized export proceeds." /> :
                            <Columns data={agingBuckets.map(b => ({ label: b.bucketLabel, value: b.amount }))}
                                yFmt={fmt} tickFmt={compactMoney} colorIndex={1} />}
                    </Card>
                )}
                {lc && (
                    <Card data-help-section="commercial.dashboard.buyerChart" title="Open LC value by buyer" sub={`Top buyers (${cur ?? '—'})`}>
                        {buyers.length === 0 ? <Empty text="No open LCs." /> : <HBars data={buyers} vFmt={compactMoney} />}
                    </Card>
                )}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                {lc && (
                    <Card data-help-section="commercial.dashboard.utilization" title="LC utilization" sub="Largest open LCs: shipped (blue) vs balance (orange)">
                        {utilRows.length === 0 ? <Empty text="No open LCs." /> :
                            <>
                                <HStack100 rows={utilRows} seriesLabels={['Shipped', 'Balance']} />
                                <Typography variant="caption" color="text.secondary">Shipped = EXP declared against the LC.</Typography>
                            </>}
                    </Card>
                )}
                {lc && (
                    <Card data-help-section="commercial.dashboard.expiryChart" title="LC expiries by month" sub={`Open LC value expiring (${cur ?? '—'})`}>
                        {expiryByMonth.length === 0 ? <Empty text="No upcoming expiries." /> :
                            <Columns data={expiryByMonth} yFmt={fmt} tickFmt={compactMoney} />}
                    </Card>
                )}
                {inc && (
                    <Card data-help-section="commercial.dashboard.claimsChart" title="Incentive claims by status" sub="Number of claims">
                        {!incentives ? <Empty text={inc.disabledReason ?? 'Cash incentives are not enabled.'} /> :
                            claimBars.length === 0 ? <Empty text="No incentive claims yet." /> : <HBars data={claimBars} colorIndex={2} />}
                    </Card>
                )}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                {lc && (
                    <Card data-help-section="commercial.dashboard.expiringList" title="Expiring soon" sub={`Open LCs expiring within ${EXPIRY_WINDOW_DAYS} days`} testId="commercial-expiring">
                        {expiring.length === 0 ? <Empty text="No LCs expiring in the next 30 days." /> : (
                            <Stack spacing={1}>
                                {expiring.slice(0, 6).map(r => (
                                    <Stack key={r.lcId} direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                                        <Link component={RouterLink} to={`/commercial/lc/${r.lcId}`}>{r.lcNumber}</Link>
                                        <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 100px' }}>{r.buyerName}</Typography>
                                        <Typography variant="body2">{r.currencyCode} {fmt(r.lcAmount)}</Typography>
                                        <Chip size="small" color={(r.daysToExpiry ?? 0) <= 7 ? 'error' : 'warning'} label={`${r.daysToExpiry} d`} />
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Card>
                )}
                {aging && (
                    <Card data-help-section="commercial.dashboard.overdueList" title="Overdue realization" sub="Oldest unrealized EXP" testId="commercial-overdue">
                        {overdueRows.length === 0 ? <Empty text="Nothing awaiting realization." /> : (
                            <Stack spacing={1}>
                                {overdueRows.map(r => (
                                    <Stack key={r.expId} direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                                        <Link component={RouterLink} to={`/commercial/exp-form/${r.expId}`}>{r.expNumber}</Link>
                                        <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 100px' }}>
                                            {r.buyerName} · {formatDate(r.expDate)}
                                        </Typography>
                                        <Typography variant="body2">{r.currencyCode} {fmt(r.unrealizedAmount)}</Typography>
                                        <Chip size="small" color={r.daysOutstanding >= OVERDUE_DAYS ? 'error' : 'warning'} label={`${r.daysOutstanding} d`} />
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Card>
                )}
            </Box>

            <Stack direction="row" gap={1} flexWrap="wrap">
                <Button data-help="commercial.dashboard.lcPosition" component={RouterLink} to="/commercial/reports/lc-position" size="small" variant="outlined">LC position report</Button>
                <Button data-help="commercial.dashboard.expAging" component={RouterLink} to="/commercial/reports/unrealized-exp-aging" size="small" variant="outlined">Unrealized EXP aging</Button>
                <Button data-help="commercial.dashboard.claimStatus" component={RouterLink} to="/commercial/reports/incentive-claim-status" size="small" variant="outlined">Incentive claim status</Button>
            </Stack>
            <MyBreakdownsDashSection />
            <MyWidgetsSection boardKey="commercial" />
        </Box>
    );
};

export default CommercialDashboard;
